import { useLocation } from "@remix-run/react";

export interface NavigationItem {
  id: string;
  label: string;
  href: string;
  iconName?: string; // Use icon name instead of component
  children?: NavigationItem[];
  requiresAuth?: boolean;
  adminOnly?: boolean;
  authRedirectMessage?: string; // 인증 안내 메시지
  matchPaths?: string[]; // Additional paths that should be considered active for this item
}

// Navigation configuration
export const navigationConfig: NavigationItem[] = [
  {
    id: "home",
    label: "홈",
    href: "/",
    iconName: "home",
  },
  {
    id: "recipes",
    label: "레시피",
    href: "/recipes",
    matchPaths: ["/recipes/"],
    iconName: "clipboard",
  },
  {
    id: "articles",
    label: "건강정보",
    href: "/articles",
    matchPaths: ["/articles/"],
    iconName: "document",
    children: [
      {
        id: "articles-slow-aging",
        label: "저속노화",
        href: "/articles/category/slow-aging",
      },
      {
        id: "articles-health-info",
        label: "건강정보",
        href: "/articles/category/health-info",
      },
      {
        id: "articles-ingredients",
        label: "식재료정보",
        href: "/articles/category/ingredients",
      },
    ],
  },
  {
    id: "shopping",
    label: "장보기",
    href: "/shopping",
    matchPaths: ["/shopping/"],
    requiresAuth: true,
    authRedirectMessage: "장보기 기능을 사용하려면 로그인이 필요합니다.",
    iconName: "shopping-cart",
  },
  {
    id: "recommendations",
    label: "맞춤형 추천",
    href: "/recommendations",
    requiresAuth: true,
    authRedirectMessage: "맞춤형 추천을 받으려면 로그인이 필요합니다.",
    iconName: "sparkles",
  },
  {
    id: "favorites",
    label: "즐겨찾기",
    href: "/favorites",
    requiresAuth: true,
    authRedirectMessage: "즐겨찾기를 확인하려면 로그인이 필요합니다.",
    iconName: "heart",
  },
  {
    id: "profile",
    label: "프로필",
    href: "/profile",
    requiresAuth: true,
    iconName: "user",
  },
  {
    id: "admin",
    label: "관리자",
    href: "/admin",
    matchPaths: ["/admin/"],
    adminOnly: true,
    iconName: "cog",
  },
];

/**
 * Check if a navigation item is active based on the current pathname
 */
export function isNavigationItemActive(
  item: NavigationItem,
  pathname: string
): boolean {
  // Exact match for home page
  if (item.href === "/" && pathname === "/") {
    return true;
  }

  // For non-home pages, check if pathname starts with the item href
  if (item.href !== "/" && pathname.startsWith(item.href)) {
    return true;
  }

  // Check additional match paths
  if (item.matchPaths) {
    return item.matchPaths.some((matchPath) => pathname.startsWith(matchPath));
  }

  // Check children for nested navigation
  if (item.children) {
    return item.children.some((child) =>
      isNavigationItemActive(child, pathname)
    );
  }

  return false;
}

/**
 * Get the active navigation item based on current pathname
 */
export function getActiveNavigationItem(
  pathname: string
): NavigationItem | null {
  for (const item of navigationConfig) {
    if (isNavigationItemActive(item, pathname)) {
      return item;
    }
  }
  return null;
}

/**
 * Hook to get current navigation state
 */
export function useNavigation() {
  const location = useLocation();
  const pathname = location.pathname;

  const activeItem = getActiveNavigationItem(pathname);

  return {
    pathname,
    activeItem,
    isActive: (item: NavigationItem) => isNavigationItemActive(item, pathname),
  };
}

/**
 * Filter navigation items based on user permissions
 */
export function filterNavigationItems(
  items: NavigationItem[],
  user: any
): NavigationItem[] {
  return items.filter((item) => {
    // Check auth requirement
    if (item.requiresAuth && !user) {
      return false;
    }

    // Check admin requirement
    if (item.adminOnly && user?.profile?.role !== "admin") {
      return false;
    }

    return true;
  });
}

/**
 * Get breadcrumb items for the current page
 */
export function getBreadcrumbs(pathname: string): NavigationItem[] {
  const breadcrumbs: NavigationItem[] = [];

  // Always start with home
  const homeItem = navigationConfig.find((item) => item.id === "home");
  if (homeItem && pathname !== "/") {
    breadcrumbs.push(homeItem);
  }

  // Check if this is a child page first
  for (const parentItem of navigationConfig) {
    if (parentItem.children) {
      const childItem = parentItem.children.find((child) =>
        isNavigationItemActive(child, pathname)
      );
      if (childItem) {
        breadcrumbs.push(parentItem);
        breadcrumbs.push(childItem);
        return breadcrumbs;
      }
    }
  }

  // Find the active item and build breadcrumb trail
  const activeItem = getActiveNavigationItem(pathname);
  if (activeItem && activeItem.id !== "home") {
    // If not a child item, just add the active item
    breadcrumbs.push(activeItem);
  }

  return breadcrumbs;
}

/**
 * 인증 필요 여부 확인 유틸리티
 */
export const getNavigationItemAuthStatus = (
  itemId: string
): {
  requiresAuth: boolean;
  message?: string;
} => {
  const item = navigationConfig.find((nav) => nav.id === itemId);
  return {
    requiresAuth: item?.requiresAuth || false,
    message: item?.authRedirectMessage,
  };
};

/**
 * 경로로 네비게이션 아이템의 인증 상태 확인
 */
export const getNavigationItemByPath = (
  href: string
): NavigationItem | null => {
  return navigationConfig.find((item) => item.href === href) || null;
};
